import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Layers } from "lucide-react";
import api from "@/lib/api";
import { computeAssembly } from "@/utils/assemblyCalc";
import { formatINR } from "@/utils/formatINR";
import AssemblyCompareView from "@/components/AssemblyCompareView";

export default function AssemblyCompare() {
  const [params, setParams] = useSearchParams();
  const [assemblies, setAssemblies] = useState([]);
  const [leftAsm, setLeftAsm] = useState(null);
  const [leftComponents, setLeftComponents] = useState([]);
  const [rightAsm, setRightAsm] = useState(null);
  const [rightComponents, setRightComponents] = useState([]);
  const [loadingSide, setLoadingSide] = useState(null);

  const leftId = params.get("a") || "";
  const rightId = params.get("b") || "";

  useEffect(() => {
    api.get("/assemblies", { params: { limit: 500 } })
      .then((r) => setAssemblies(r.data.assemblies || []))
      .catch(() => {});
  }, []);

  const loadSide = async (id, setAsm, setComps, sideTag) => {
    if (!id) { setAsm(null); setComps([]); return; }
    setLoadingSide(sideTag);
    try {
      const r = await api.get(`/assemblies/${id}`);
      setAsm(r.data);
      const componentIds = (r.data.components || []).map((c) => c.npd_id).filter(Boolean);
      if (componentIds.length === 0) { setComps([]); return; }
      const comps = await Promise.all(componentIds.map((nid) => api.get(`/npd/${nid}`).then((x) => x.data).catch(() => null)));
      setComps(comps.filter(Boolean));
    } finally { setLoadingSide(null); }
  };

  useEffect(() => { loadSide(leftId, setLeftAsm, setLeftComponents, "left"); /* eslint-disable-next-line */ }, [leftId]);
  useEffect(() => { loadSide(rightId, setRightAsm, setRightComponents, "right"); /* eslint-disable-next-line */ }, [rightId]);

  const sortedAsm = useMemo(() => [...assemblies].sort((a, b) => (b.assembly_number || "").localeCompare(a.assembly_number || "")), [assemblies]);

  const setLeft = (id) => { const next = new URLSearchParams(); if (id) next.set("a", id); if (rightId) next.set("b", rightId); setParams(next); };
  const setRight = (id) => { const next = new URLSearchParams(); if (leftId) next.set("a", leftId); if (id) next.set("b", id); setParams(next); };
  const swap = () => { const next = new URLSearchParams(); if (rightId) next.set("a", rightId); if (leftId) next.set("b", leftId); setParams(next); };

  const leftCalc = useMemo(() => (leftAsm ? computeAssembly(leftAsm, leftComponents) : null), [leftAsm, leftComponents]);
  const rightCalc = useMemo(() => (rightAsm ? computeAssembly(rightAsm, rightComponents) : null), [rightAsm, rightComponents]);
  const sameSelected = leftId && rightId && leftId === rightId;

  return (
    <div className="p-4 lg:p-6 max-w-[1400px] mx-auto" data-testid="assembly-compare-page">
      <div className="flex items-center gap-3 mb-4">
        <Link to="/assemblies" className="p-2 hover:bg-slate-100 rounded-md text-slate-500"><ArrowLeft size={18} /></Link>
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2"><Layers size={20} className="text-orange-600" /> Compare two assemblies</h1>
          <p className="text-xs text-slate-500 mt-0.5">Cross-assembly comparison — pick any two live assemblies. Useful for design alternative analysis.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Left assembly</div>
          <select value={leftId} onChange={(e) => setLeft(e.target.value)} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm bg-white" data-testid="asm-compare-left-picker">
            <option value="">Pick left assembly…</option>
            {sortedAsm.map((a) => (
              <option key={a.assembly_id} value={a.assembly_id}>{a.assembly_number} — {a.assembly_name}</option>
            ))}
          </select>
          {leftAsm && leftCalc && (
            <div className="mt-3 text-xs text-slate-700">
              <div className="font-mono text-slate-500">{leftAsm.assembly_number}</div>
              <div className="font-medium">{leftAsm.assembly_name}</div>
              <div className="mt-1 font-mono text-base text-slate-900">{formatINR(leftCalc.grand_total)}</div>
            </div>
          )}
          {loadingSide === "left" && <div className="mt-2 text-xs text-slate-400 italic">Loading…</div>}
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Right assembly</div>
            {leftId && rightId && !sameSelected && (
              <button onClick={swap} className="text-[10px] text-sky-700 hover:text-sky-900 underline" data-testid="asm-compare-swap">Swap ↔</button>
            )}
          </div>
          <select value={rightId} onChange={(e) => setRight(e.target.value)} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm bg-white" data-testid="asm-compare-right-picker">
            <option value="">Pick right assembly…</option>
            {sortedAsm.map((a) => (
              <option key={a.assembly_id} value={a.assembly_id}>{a.assembly_number} — {a.assembly_name}</option>
            ))}
          </select>
          {rightAsm && rightCalc && (
            <div className="mt-3 text-xs text-slate-700">
              <div className="font-mono text-slate-500">{rightAsm.assembly_number}</div>
              <div className="font-medium">{rightAsm.assembly_name}</div>
              <div className="mt-1 font-mono text-base text-slate-900">{formatINR(rightCalc.grand_total)}</div>
            </div>
          )}
          {loadingSide === "right" && <div className="mt-2 text-xs text-slate-400 italic">Loading…</div>}
        </div>
      </div>

      {sameSelected && (
        <div className="bg-amber-50 border border-amber-200 rounded-md px-4 py-2 text-xs text-amber-800 mb-4">Pick two different assemblies to compare.</div>
      )}

      {leftAsm && rightAsm && !sameSelected && (
        <AssemblyCompareView
          round={{
            round_id: `xcompare-${leftAsm.assembly_id}`,
            round_label: leftAsm.assembly_number,
            round_date: leftAsm.created_at || new Date().toISOString(),
            notes: "",
            created_by_name: "",
            snapshot: {
              assembly: leftAsm,
              components: leftComponents,
              assembly_grand_total_at_save: leftCalc ? leftCalc.grand_total : 0,
            },
          }}
          currentAsm={rightAsm}
          currentComponents={rightComponents}
          rightLabel={rightAsm.assembly_number}
        />
      )}

      {!leftAsm && !rightAsm && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-10 text-center text-sm text-slate-500">
          Pick two assemblies above to see a side-by-side cost comparison.
        </div>
      )}
    </div>
  );
}
